import { Injectable } from '@angular/core';
import { ProcessApiService } from '@app/core/services/process-api.service';
import { RadioState } from '@app/core/states/radio.state';
import { Playlist } from '@app/library/models/playlist.model';
import { PlaylistService } from '@app/library/services/playlist.service';
import { PlaylistColumnsState } from '@app/library/states/playlist-columns.state';
import {
  DeletePlaylistTracksSuccess,
  PlaylistTracksSuccess,
} from '@app/library/states/playlist-tracks.actions';
import {
  CreatePlaylistDynamicFailure,
  CreatePlaylistDynamicRequest,
  CreatePlaylistDynamicSuccess,
  DeletePlaylistFailure,
  DeletePlaylistRequest,
  DeletePlaylistSuccess,
  DuplicatePlaylistFailure,
  DuplicatePlaylistRequest,
  DuplicatePlaylistSuccess,
  EditPlaylistDynamicRequest,
  EditPlaylistDynamicSuccess,
  ForceAddToPlaylistRequest,
  GetPlaylistFailure,
  GetPlaylistRequest,
  GetPlaylistSuccess,
  ResumePlaylistProcess,
  SetActionAfterEditPlaylist,
  UpdatePlaylistTracksCountAfterDelete,
} from '@app/library/states/playlists.actions';
import { TracksAddToPlaylistSuccess } from '@app/library/states/tracks.actions';
import { Navigate } from '@ngxs/router-plugin';
import {
  Action,
  Actions,
  ofActionDispatched,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { CloseCurrentPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { deleteKey, removeItemWithId } from '@radioking/xs-state-operators';
import amplitude from 'amplitude-js';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, mergeMap, take, tap } from 'rxjs/operators';

import {
  AddToPlaylistFailure,
  AddToPlaylistRequest,
  AddToPlaylistSuccess,
  AddToPlaylistWaitDuplicates,
  CreatePlaylistManualFailure,
  CreatePlaylistManualRequest,
  CreatePlaylistManualSuccess,
  EditPlaylistManualFailure,
  EditPlaylistManualRequest,
  EditPlaylistManualSuccess,
  EndEditPlaylist,
  PlaylistsFailure,
  PlaylistsRequest,
  PlaylistsSuccess,
  StartEditPlaylist,
  UpdatePlaylist,
} from './playlists.actions';

export class PlaylistsStateModel {
  playlist: Playlist[];
  isFetching: boolean;
  editingPlaylist: Playlist;
  updatingPlaylistTracksCount: number;
  process: { [key: string]: string };
  actionAfterEdit: any;
}

@State<PlaylistsStateModel>({
  name: 'playlists',
  defaults: {
    playlist: null,
    isFetching: false,
    editingPlaylist: null,
    updatingPlaylistTracksCount: null,
    process: {},
    actionAfterEdit: null,
  },
  children: [PlaylistColumnsState],
})
@Injectable()
export class PlaylistsState {
  constructor(
    private readonly action$: Actions,
    private readonly store: Store,
    private readonly playlistService: PlaylistService,
    private readonly processApiService: ProcessApiService,
  ) {}

  @Selector()
  static playlists(state: PlaylistsStateModel): Playlist[] {
    return state.playlist;
  }

  @Selector()
  static manualPlaylistsToSuggestion(state: PlaylistsStateModel): SugestionInput[] {
    return state.playlist
      .filter(playlist => playlist.type === 'manual')
      .map(playlist => ({
        id: playlist.id,
        name: playlist.name,
        color: playlist.color,
        type: playlist.type,
      }));
  }

  @Selector()
  static isPlaylistLoaded(state: PlaylistsStateModel): boolean {
    return !!state.playlist;
  }

  @Selector()
  static isLoading(state: PlaylistsStateModel) {
    return state.isFetching;
  }

  @Selector()
  static editingPlaylist(state: PlaylistsStateModel) {
    return state.editingPlaylist;
  }

  @Selector()
  static playlistByID(state: PlaylistsStateModel) {
    return (id: number): Playlist => state.playlist.find(pl => pl.id === id);
  }

  @Selector()
  static updatingPlaylistTracksCount(state: PlaylistsStateModel): number {
    return state.updatingPlaylistTracksCount;
  }

  @Selector()
  static firstManualPlaylist(state: PlaylistsStateModel): SugestionInput {
    const manuals = state.playlist.filter(playlist => playlist.type === 'manual');

    return manuals.length
      ? {
          id: manuals[0].id,
          name: manuals[0].name,
          color: manuals[0].color,
          type: 'manual',
        }
      : null;
  }

  @Selector()
  static filteredPlaylists(state: PlaylistsStateModel): (search: string) => Playlist[] {
    return search =>
      state.playlist.filter(playlist => playlist.name.indexOf(search) > -1);
  }

  @Selector()
  static playlistProcessing(state: PlaylistsStateModel): (id: number) => boolean {
    return id => {
      const key = `playlist${id}`;

      return !!state.process[key];
    };
  }

  @Selector()
  static editingPlaylistType(state: PlaylistsStateModel): string {
    return state.editingPlaylist ? state.editingPlaylist.type : null;
  }

  @Action(PlaylistsRequest)
  getPlaylist(ctx: StateContext<PlaylistsStateModel>, { noLoader }: PlaylistsRequest) {
    if (ctx.getState().isFetching) {
      return this.action$.pipe(ofActionDispatched(PlaylistsFailure, PlaylistsSuccess));
    }
    if (!noLoader) {
      ctx.patchState({ isFetching: true });
    }

    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.getRadioPlaylists(id)),
      mergeMap(data => ctx.dispatch(new PlaylistsSuccess(data))),
      catchError(err => ctx.dispatch(new PlaylistsFailure(err))),
    );
  }

  @Action(PlaylistsSuccess)
  getPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: PlaylistsSuccess,
  ) {
    ctx.patchState({ playlist, isFetching: false });
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }

  @Action(GetPlaylistRequest)
  getPlaylistById(
    ctx: StateContext<PlaylistsStateModel>,
    { idPlaylist }: GetPlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.getPlaylistDetails(id, idPlaylist)),
      mergeMap(playlist => ctx.dispatch(new GetPlaylistSuccess(playlist))),
      catchError(err => ctx.dispatch(new GetPlaylistFailure(err))),
    );
  }

  @Action(GetPlaylistSuccess)
  getPlaylistByIdSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: GetPlaylistSuccess,
  ) {
    ctx.patchState({ editingPlaylist: playlist });
  }

  @Action(CreatePlaylistManualRequest)
  createPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist, tracks }: CreatePlaylistManualRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.createPlaylist(id, playlist)),
      mergeMap(data => ctx.dispatch(new CreatePlaylistManualSuccess(data, tracks))),
      catchError(err => ctx.dispatch(new CreatePlaylistManualFailure(err))),
    );
  }

  @Action(CreatePlaylistManualSuccess)
  createPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist, withTracks }: CreatePlaylistManualSuccess,
  ) {
    const eventProperties = {
      type: 'manual playlist',
    };
    amplitude.getInstance().logEvent('playlist added', eventProperties);
    ctx.dispatch(
      new Navigate(['radio', playlist.idRadio, 'media', 'playlist', playlist.id]),
    );
    ctx.dispatch(new PlaylistsRequest());
    ctx.dispatch(new CloseCurrentPanel(true));
    if (withTracks) {
      ctx.dispatch(new AddToPlaylistRequest(playlist.id, withTracks));
    }
  }

  @Action(CreatePlaylistDynamicRequest)
  createDynamicPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: CreatePlaylistDynamicRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.createPlaylist(id, playlist)),
      mergeMap(data => ctx.dispatch(new CreatePlaylistDynamicSuccess(data))),
      catchError(err => ctx.dispatch(new CreatePlaylistDynamicFailure(err))),
    );
  }

  @Action(CreatePlaylistDynamicSuccess)
  createDynamicPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: CreatePlaylistDynamicSuccess,
  ) {
    const eventProperties = {
      type: 'automatic playlist',
    };
    amplitude.getInstance().logEvent('playlist added', eventProperties);
    const key = `playlist${playlist.id}`;
    ctx.setState(
      patch({
        process: patch({ [key]: playlist.process }),
      }),
    );
    ctx.dispatch(
      new Navigate(['radio', playlist.idRadio, 'media', 'playlist', playlist.id]),
    );
    ctx.dispatch(new PlaylistsRequest());
    ctx.dispatch(new CloseCurrentPanel(true));

    return this.playlistProcess(
      ctx,
      playlist.idRadio,
      playlist.id,
      key,
      playlist.process,
    );
  }

  @Action([CreatePlaylistManualSuccess, CreatePlaylistDynamicSuccess])
  appendNewPlaylistCreated(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: CreatePlaylistManualSuccess | CreatePlaylistDynamicSuccess,
  ) {
    ctx.setState(
      patch({
        playlist: append([playlist]),
      }),
    );
  }

  @Action(PlaylistTracksSuccess)
  getPlaylistTracksSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { idPlaylist, tracks }: PlaylistTracksSuccess,
  ) {
    ctx.setState(
      patch({
        playlist: updateItem<Playlist>(
          it => it.id === idPlaylist,
          patch({
            nbTracks: tracks.length,
          }),
        ),
      }),
    );
  }

  @Action(StartEditPlaylist)
  startEditPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: StartEditPlaylist,
  ) {
    if (playlist.type === 'dynamic') {
      ctx.dispatch(new GetPlaylistRequest(playlist.id));
    }
    ctx.patchState({ editingPlaylist: playlist });
  }

  @Action(EndEditPlaylist)
  endEditPlaylist(ctx: StateContext<PlaylistsStateModel>) {
    ctx.dispatch(new CloseCurrentPanel(true));
    ctx.patchState({ editingPlaylist: null });
  }

  @Action(EditPlaylistManualRequest)
  editPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlistId, playlist }: EditPlaylistManualRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.editPlaylist(id, playlistId, playlist)),
      mergeMap(() => ctx.dispatch(new EditPlaylistManualSuccess())),
      catchError(err => ctx.dispatch(new EditPlaylistManualFailure(err))),
    );
  }

  @Action(EditPlaylistManualSuccess)
  editPlaylistSuccess(ctx: StateContext<PlaylistsStateModel>): Observable<void> | void {
    ctx.dispatch(new PlaylistsRequest());
    if (ctx.getState().actionAfterEdit) {
      return ctx.dispatch(new CloseCurrentPanel()).pipe(
        mergeMap(() => {
          const tmpAction = ctx.getState().actionAfterEdit;
          ctx.patchState({ actionAfterEdit: null });

          return ctx.dispatch(tmpAction);
        }),
      );
    } else {
      ctx.dispatch(new CloseCurrentPanel(true));
    }
  }

  @Action(EditPlaylistManualFailure)
  editPlaylistManualFailure(
    ctx: StateContext<PlaylistsStateModel>,
    { error }: EditPlaylistManualFailure,
  ) {
    if (error.status === 304) {
      this.store.dispatch(new EndEditPlaylist());
    }
  }

  @Action(EditPlaylistDynamicRequest)
  editDynamicPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlistId, playlist }: EditPlaylistDynamicRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.editPlaylist(id, playlistId, playlist)),
      mergeMap(data => ctx.dispatch(new EditPlaylistDynamicSuccess(data))),
      catchError(err => ctx.dispatch(new EditPlaylistManualFailure(err))),
    );
  }

  @Action(EditPlaylistDynamicSuccess)
  editDynamicPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: EditPlaylistDynamicSuccess,
  ) {
    const key = `playlist${playlist.id}`;
    ctx.setState(
      patch({
        process: patch({ [key]: playlist.process }),
      }),
    );

    ctx.dispatch(new PlaylistsRequest());
    if (ctx.getState().actionAfterEdit) {
      return ctx.dispatch(new CloseCurrentPanel()).pipe(
        mergeMap(() => {
          const tmpAction = ctx.getState().actionAfterEdit;
          ctx.patchState({ actionAfterEdit: null });

          return ctx.dispatch(tmpAction);
        }),
        mergeMap(() =>
          this.playlistProcess(ctx, playlist.idRadio, playlist.id, key, playlist.process),
        ),
      );
    } else {
      ctx.dispatch(new CloseCurrentPanel(true));
    }

    return this.playlistProcess(
      ctx,
      playlist.idRadio,
      playlist.id,
      key,
      playlist.process,
    );
  }

  @Action(AddToPlaylistRequest)
  addToPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlistId, tracks, position }: AddToPlaylistRequest,
  ) {
    const currentPlaylist = ctx.getState().playlist.find(pl => pl.id === playlistId);
    const actualPlaylistLength = currentPlaylist ? currentPlaylist.nbTracks : 0;
    ctx.patchState({
      updatingPlaylistTracksCount: playlistId,
    });

    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.playlistService.addToPlaylist(id, playlistId, tracks, null, position),
      ),
      mergeMap(newTracks => {
        ctx.dispatch(new TracksAddToPlaylistSuccess(currentPlaylist, tracks));

        return ctx.dispatch(
          new AddToPlaylistSuccess(
            playlistId,
            newTracks,
            {
              count: newTracks.length - actualPlaylistLength,
            },
            position,
          ),
        );
      }),
      catchError(err =>
        ctx.dispatch(new AddToPlaylistFailure(err, playlistId, tracks, position)),
      ),
    );
  }

  @Action(AddToPlaylistSuccess)
  addToPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlistId, tracks }: AddToPlaylistSuccess,
  ) {
    const currentPlaylist = ctx
      .getState()
      .playlist.find(playlist => playlist.id === playlistId);
    currentPlaylist.nbTracks = tracks.length;
    ctx.dispatch(new UpdatePlaylist(currentPlaylist));
  }

  @Action(DeletePlaylistTracksSuccess)
  removeToPlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { idPlaylist, tracks }: DeletePlaylistTracksSuccess,
  ) {
    const currentPlaylist = ctx
      .getState()
      .playlist.find(playlist => playlist.id === idPlaylist);
    currentPlaylist.nbTracks = tracks.length;
    ctx.dispatch(new UpdatePlaylist(currentPlaylist));
  }

  @Action(AddToPlaylistFailure)
  addToPlaylistFailure(
    ctx: StateContext<PlaylistsStateModel>,
    { error, playlistId, tracks, position }: AddToPlaylistFailure,
  ) {
    if (error.status === 409) {
      this.store.dispatch(new AddToPlaylistWaitDuplicates(playlistId, tracks, position));
    }
    ctx.patchState({
      updatingPlaylistTracksCount: null,
    });
  }

  @Action(ForceAddToPlaylistRequest)
  forceAddToPlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlistId, tracks, mode, position }: ForceAddToPlaylistRequest,
  ) {
    const currentPlaylist = ctx.getState().playlist.find(pl => pl.id === playlistId);
    ctx.patchState({
      updatingPlaylistTracksCount: playlistId,
    });

    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.playlistService.addToPlaylist(id, playlistId, tracks, mode, position),
      ),
      mergeMap(added => {
        ctx.dispatch(new TracksAddToPlaylistSuccess(currentPlaylist, tracks));

        return ctx.dispatch(
          new AddToPlaylistSuccess(playlistId, added, { count: tracks.length }, position),
        );
      }),
      catchError(err => ctx.dispatch(new AddToPlaylistFailure(err, playlistId, tracks))),
    );
  }

  @Action(UpdatePlaylist)
  refreshPlaylistNbTracks(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: UpdatePlaylist,
  ) {
    let playlists = ctx.getState().playlist;
    playlists = playlists.map(pl => {
      if (pl.id !== playlist.id) {
        return pl;
      }

      return playlist;
    });
    ctx.patchState({
      playlist: playlists,
      updatingPlaylistTracksCount: null,
    });
  }

  @Action(DeletePlaylistRequest)
  deletePlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: DeletePlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.deletePlaylist(id, playlist.id)),
      mergeMap(() => ctx.dispatch(new DeletePlaylistSuccess(playlist))),
      catchError(err => ctx.dispatch(new DeletePlaylistFailure(err))),
    );
  }

  @Action(DeletePlaylistSuccess)
  deletePlaylistSuccess(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: DeletePlaylistSuccess,
  ) {
    ctx.dispatch(new Navigate(['radio', playlist.idRadio, 'media', 'bac', 1]));
    ctx.setState(
      patch({
        playlist: removeItemWithId(playlist),
      }),
    );
  }

  @Action(DuplicatePlaylistRequest)
  duplicatePlaylist(
    ctx: StateContext<PlaylistsStateModel>,
    { playlist }: DuplicatePlaylistRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.duplicatePlaylist(id, playlist)),
      mergeMap(data => {
        ctx.dispatch(new DuplicatePlaylistSuccess());
        if (data.type === 'manual') {
          return ctx.dispatch(new CreatePlaylistManualSuccess(data));
        } else {
          return ctx.dispatch(new CreatePlaylistDynamicSuccess(data));
        }
      }),
      catchError(err => ctx.dispatch(new DuplicatePlaylistFailure(err))),
    );
  }

  @Action(ResumePlaylistProcess)
  resumeProcess(ctx: StateContext<PlaylistsStateModel>) {
    const processes = ctx.getState().process;

    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        const obs = Object.entries(processes).map(([key, value]) => {
          const idPlaylist = parseInt(key.replace('playlist', ''), 10);

          return this.playlistProcess(ctx, id, idPlaylist, key, value);
        });

        return combineLatest(obs);
      }),
    );
  }

  @Action(UpdatePlaylistTracksCountAfterDelete)
  updatePlaylistTracksCount(
    ctx: StateContext<PlaylistsStateModel>,
    { idPlaylist, numberToRemove }: UpdatePlaylistTracksCountAfterDelete,
  ) {
    const pl = ctx.getState().playlist.find(p => p.id === idPlaylist);
    pl.nbTracks = numberToRemove;
    ctx.setState(
      patch<PlaylistsStateModel>({
        playlist: updateItem(p => p.id === idPlaylist, pl),
      }),
    );
  }

  @Action(SetActionAfterEditPlaylist)
  actionAfterEdit(
    ctx: StateContext<PlaylistsStateModel>,
    { action }: SetActionAfterEditPlaylist,
  ) {
    ctx.patchState({ actionAfterEdit: action });
  }

  playlistProcess(
    ctx: StateContext<PlaylistsStateModel>,
    idRadio: number,
    idPlaylist: number,
    key: string,
    process: string,
  ): Observable<any> {
    return this.processApiService
      .waitForPlaylistProcess(idRadio, idPlaylist, process)
      .pipe(
        catchError(() => of('')),
        tap(() => {
          ctx.setState(
            patch({
              process: deleteKey(key),
            }),
          );
        }),
      );
  }
}
